import { map, uniq, compose, isEmpty } from 'lodash/fp';
import { array } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { createAction } from '@mews/framework';
import { ApplicationState, CommanderThunkAction, WithAvailabilityBlockId } from '@mews-commander/types';
import { AvailabilityAdjustment } from '@mews-commander/data-access';
import { mapId } from '@mews-commander/utils';
import moduleSelector from '../selector';
import prefix from '../prefix';
import { createEntitiesReducer, createBaseEntitySelectors, createEntitySelectorByAvailabilityBlockId } from './utils';

export const NAME = 'availabilityAdjustments';

const AVAILABILITY_ADJUSTMENTS_LOADED = prefix('AVAILABILITY_ADJUSTMENTS_LOADED');
const AVAILABILITY_ADJUSTMENTS_DELETED = prefix('AVAILABILITY_ADJUSTMENTS_DELETED');

export const setAvailabilityAdjustments = (availabilityAdjustments: AvailabilityAdjustment[]) =>
    createAction(AVAILABILITY_ADJUSTMENTS_LOADED, { entities: availabilityAdjustments });

export const deleteAvailabilityAdjustments = (adjustmentIds: string[]) =>
    createAction(AVAILABILITY_ADJUSTMENTS_DELETED, adjustmentIds);

export const deleteAvailabilityAdjustmentsByBlockIds =
    (blockIds: string[]): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();

        const adjustmentIds = flow(
            array.map((availabilityBlockId: string) =>
                getAvailabilityAdjustmentsByAvailabilityBlockId(state, { availabilityBlockId })
            ),
            array.flatten,
            mapId
        )(blockIds);

        return dispatch(deleteAvailabilityAdjustments(adjustmentIds));
    };

export const availabilityAdjustmentsReducer = createEntitiesReducer({
    FETCH_SUCCESS: AVAILABILITY_ADJUSTMENTS_LOADED,
    DELETE_SUCCESS: AVAILABILITY_ADJUSTMENTS_DELETED,
});

export const [getAvailabilityAdjustments] = createBaseEntitySelectors<
    ApplicationState,
    AvailabilityAdjustment,
    typeof NAME
>(moduleSelector, NAME);

export const getAvailabilityAdjustmentsByAvailabilityBlockId =
    createEntitySelectorByAvailabilityBlockId(getAvailabilityAdjustments);

const getUniqAdjustments = compose(uniq, map('resourceCategoryId'));

export const getAvailabilityAdjustmentsUniqResourceCategoryIds = compose<
    ApplicationState,
    WithAvailabilityBlockId,
    string[],
    Array<AvailabilityAdjustment>
>(getUniqAdjustments, getAvailabilityAdjustmentsByAvailabilityBlockId);

export const hasAvailabilityBlockAdjustments = compose(isEmpty, getAvailabilityAdjustmentsByAvailabilityBlockId);
